import type { ReactNode } from 'react';
import { IconPencil } from '@tabler/icons-react';
import { Paper } from '../primitives/Paper.js';
import { IconButton } from '../primitives/IconButton.js';
import { WP_ICON_NAV } from '../icons/constants.js';
import { cx } from '../utils/cx.js';
import styles from './DetailBento.module.css';

export type DetailFieldViewProps = {
  label?: string;
  children: ReactNode;
  /** Hover / focus reveals a pencil; click starts atomic edit. */
  onEdit?: () => void;
  /** When true, render `edit` instead of the read-only value. */
  editing?: boolean;
  edit?: ReactNode;
};

/** Read-only value (and optional inner label). Card-level labels live on `DetailAtomCard`. */
export function DetailFieldView({ label, children, onEdit, editing = false, edit }: DetailFieldViewProps) {
  if (editing && edit) {
    return <div className={styles.fieldEdit}>{edit}</div>;
  }

  if (!label && !onEdit) {
    return <div className={styles.valueFlush}>{children}</div>;
  }

  return (
    <div className={cx(styles.field, onEdit && styles.fieldEditable)}>
      <div className={styles.fieldHead}>
        {label ? <span className={styles.label}>{label}</span> : <span />}
        {onEdit ? (
          <IconButton
            type="button"
            variant="ghost"
            size="xs"
            className={styles.fieldEditBtn}
            aria-label={`Modifier ${label ?? ''}`.trim()}
            onClick={onEdit}
          >
            <IconPencil {...WP_ICON_NAV} />
          </IconButton>
        ) : null}
      </div>
      <div className={styles.value}>{children}</div>
    </div>
  );
}

export type DetailFieldClusterProps = {
  editing?: boolean;
  children: ReactNode;
};

/** Groups dependent fields (e.g. type + catégorie) while one of them is being edited. */
export function DetailFieldCluster({ editing = false, children }: DetailFieldClusterProps) {
  return <div className={cx(styles.cluster, editing && styles.clusterEditing)}>{children}</div>;
}

export type DetailHeroStatProps = {
  label: string;
  children: ReactNode;
};

/** Top-row stat tile of a detail page. */
export function DetailHeroStat({ label, children }: DetailHeroStatProps) {
  return (
    <Paper withBorder p="md" radius="md">
      <span className={styles.label}>{label}</span>
      <div className={styles.value}>{children}</div>
    </Paper>
  );
}

export type DetailAtomCardProps = {
  /** Outlined legend sitting on the top border (Material). */
  label?: string;
  emphasized?: boolean;
  onEdit?: () => void;
  titleExtra?: ReactNode;
  children: ReactNode;
};

/** Card around one editable field, or a cluster of interdependent fields. */
export function DetailAtomCard({
  label,
  emphasized = false,
  onEdit,
  titleExtra,
  children,
}: DetailAtomCardProps) {
  if (!label) {
    return (
      <Paper
        withBorder
        p="md"
        radius="md"
        shadow={emphasized ? 'md' : 'none'}
        className={cx(styles.card, emphasized && styles.cardEmphasized)}
      >
        {children}
      </Paper>
    );
  }

  return (
    <div className={styles.atomShell}>
    <fieldset className={cx(styles.atom, emphasized && styles.atomEmphasized, onEdit && styles.atomEditable)}>
      <legend className={styles.atomLegend}>
        <span className={styles.atomLegendText}>{label}</span>
        {onEdit ? (
          <IconButton
            type="button"
            variant="ghost"
            size="xs"
            className={styles.fieldEditBtn}
            aria-label={`Modifier ${label}`}
            onClick={onEdit}
          >
            <IconPencil {...WP_ICON_NAV} />
          </IconButton>
        ) : (
          titleExtra
        )}
      </legend>
      <div className={styles.atomBody}>{children}</div>
    </fieldset>
    </div>
  );
}

export type DetailSectionCardProps = {
  title: string;
  /** Trailing content in the section header (badge, count, custom control). */
  titleExtra?: ReactNode;
  /** Adds a pencil affordance in the header; pair with `emphasized` for inline edit. */
  onEdit?: () => void;
  /** Edit mode: lifts the card and renders `children` as the edit form. */
  emphasized?: boolean;
  children: ReactNode;
};

/** Bento section card — the detail-page building block. */
export function DetailSectionCard({
  title,
  titleExtra,
  onEdit,
  emphasized = false,
  children,
}: DetailSectionCardProps) {
  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      shadow={emphasized ? 'md' : 'none'}
      className={cx(styles.card, emphasized && styles.cardEmphasized)}
    >
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>{title}</span>
        {onEdit ? (
          <IconButton type="button" variant="ghost" size="xs" aria-label={`Modifier ${title}`} onClick={onEdit}>
            <IconPencil {...WP_ICON_NAV} />
          </IconButton>
        ) : (
          titleExtra
        )}
      </div>
      {children}
    </Paper>
  );
}
